/**
 * The row hover / right-click menu (spec M1-B3 §8.3; **re-measured in B10**,
 * `[c:item-menu]` / `[c:item-menu-move-to]`).
 *
 * monday's hover menu is **220 × 420**, 32 px rows, 17 px group gaps, and holds exactly:
 *
 *   Open item · Open in new tab
 *   ─
 *   Copy item link · Move to ▸ · Duplicate · Create new item below
 *   ─
 *   Add subitem · Convert to subitem
 *   ─
 *   Apps                                   ← monday-only, absent here
 *   ─
 *   Archive · Delete
 *
 *   Move to ▸   Move to top · Move to group ▸ · Move to board          (240 × 96)
 *
 * Round 3 had put `Copy name` / `Paste` at the top of the *hover* menu; they belong to the
 * right-click menu only, which is one row taller than the hover menu (220 × 452 `[ui:item-02]`).
 * `Move to`, `Create new item below`, `Add subitem` and `Convert to subitem` were all rendered
 * disabled although every call behind them existed — that is what B10 closes.
 */
import type { BoardId, GroupId, ItemId } from '@elizmonday/shared';
import { useTable, useTableActions, useTableContext } from '../context';
import { Menu, type MenuEntry } from './Menu';

/**
 * **P-304 — a subitem's row menu is not an item's.**
 *
 * Nico: *"I cannot cancel subitems"*. The `⋯` on a nested row opened the **item** menu (Open
 * item, Add subitem, Convert to subitem, …) and its Delete did nothing at all — no dialog, no
 * toast, the row still there — because `store.removeItems` named the **parent board** and a
 * subitem lives on the board's linked subitems board `[S1§2]`.
 *
 * monday's own menu was re-captured on the sandbox board for this round
 * (`discovery/ui-reference/subitems/05-subitem-row-menu.json`, 2026-09-24, 236 × 404 at
 * (363, 445); the `ul` is 220 wide, rows 32 px) and is, in order, with its dividers:
 *
 *   Open subitem · Open in new tab
 *   ─
 *   Copy subitem link · Move to Item ▸ · Duplicate ▸ · Create new subitem below
 *   ─
 *   Convert to item
 *   ─
 *   Apps ▸                                  ← monday-only, absent here as in the item menu
 *   ─
 *   Archive · Delete
 *
 * Nothing here is invented: every label is the captured one. What the capture does **not**
 * carry is the contents of monday's `Duplicate ▸` and `Apps ▸` flyouts (the JSON holds the top
 * level only), so `Duplicate` acts on the row like the item menu's does rather than opening a
 * flyout we have never seen — the conservative reading (CLAUDE.md rule 3, **q-S1-6**).
 */
function subitemEntries({
  itemId,
  parentItemId,
  boardId,
  subitemsBoardId,
  candidates,
  actions,
  onOpenItem,
  close,
}: {
  itemId: ItemId;
  parentItemId: ItemId;
  boardId: string;
  subitemsBoardId: BoardId | null;
  candidates: readonly { id: ItemId; name: string }[];
  actions: ReturnType<typeof useTableActions>;
  onOpenItem(): void;
  close(): void;
}): MenuEntry[] {
  /** The subitem's own page is the **parent board's** route `[uis:12-subitems-board-direct]`. */
  const href = `/boards/${boardId}/pulses/${String(itemId)}`;
  /*
   * Where the write lands. Until the first subitem exists there is no subitems board, but this
   * menu is only ever opened *on* a subitem, so the id is known by then; a source that has not
   * reported one yet leaves the destructive rows disabled rather than writing to the wrong
   * board — which is the defect this card is named for.
   */
  const target = subitemsBoardId === null ? null : { boardId: subitemsBoardId, parentItemId };
  const noBoard = target === null ? 'The subitems board is not loaded yet' : undefined;

  return [
    { id: 'open', label: 'Open subitem', onSelect: onOpenItem },
    {
      id: 'open-tab',
      label: 'Open in new tab',
      onSelect: () => window.open(href, '_blank'),
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'copy-link',
      label: 'Copy subitem link',
      onSelect: () =>
        void navigator.clipboard?.writeText(`${window.location.origin}${href}`).catch(() => undefined),
    },
    {
      id: 'move-parent',
      label: 'Move to Item',
      submenuWidth: 240,
      submenu: candidates.map((row) => ({
        id: `parent-${String(row.id)}`,
        label: row.name,
        disabled: String(row.id) === String(parentItemId),
        onSelect: () => {
          close();
          void actions.moveSubitemToParent(itemId, row.id, parentItemId);
        },
      })),
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      disabled: target === null,
      title: noBoard,
      onSelect: () => {
        close();
        if (target) void actions.duplicateItems([itemId], target);
      },
    },
    {
      /**
       * monday's wording, and **ASSUMED** where it says *below*: `items.createSubitem` places
       * `top` or `bottom` only (`subitems.ts#createSubitemInput`), so ours appends to the
       * nested table as `+ Add subitem` does. Inserting after *this* row is a service change,
       * not a menu one, and the capture does not show what monday does with a subitem created
       * from the third row of five (q588's neighbour).
       */
      id: 'create-below',
      label: 'Create new subitem below',
      onSelect: () => {
        close();
        void actions.addSubitem(parentItemId, 'New subitem');
      },
    },
    { id: 'd2', label: '', divider: true },
    {
      id: 'convert-item',
      label: 'Convert to item',
      onSelect: () => {
        close();
        void actions.convertSubitemToItem(itemId, parentItemId);
      },
    },
    { id: 'd3', label: '', divider: true },
    {
      id: 'archive',
      label: 'Archive',
      disabled: target === null,
      title: noBoard,
      onSelect: () => {
        close();
        if (target) void actions.archiveItems([itemId], target);
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      danger: true,
      disabled: target === null,
      title: noBoard,
      onSelect: () => {
        close();
        if (target) void actions.deleteItems([itemId], target);
      },
    },
  ];
}

export function ItemMenu({
  itemId,
  x,
  y,
  onOpenItem,
  /** The right-click menu adds `Copy name`; the hover menu does not `[ui:item-02]`. */
  contextMenu = false,
}: {
  itemId: ItemId;
  x: number;
  y: number;
  onOpenItem(): void;
  contextMenu?: boolean;
}) {
  const payload = useTable((state) => state.payload);
  const actions = useTableActions();
  const { source } = useTableContext();
  /**
   * P-304: the open menu's own state says whether this row is a **subitem**, and whose. It is
   * read here rather than passed down from `TableView`, because what the menu is is a property
   * of the row that opened it and of nothing else.
   */
  const menu = useTable((state) => state.menu);
  const subitemsBoardId = useTable((state) => state.subitemsBoardId);
  const subitemsByParent = useTable((state) => state.subitemsByParent);
  const parentItemId = menu?.kind === 'item' && menu.targetId === String(itemId) ? menu.parentItemId : undefined;

  const item = payload?.groups.flatMap((group) => group.page.items).find((entry) => entry.id === itemId);
  const subitem =
    parentItemId === undefined
      ? undefined
      : (subitemsByParent[parentItemId] ?? []).find((entry) => String(entry.id) === String(itemId));
  const name = item?.name ?? subitem?.name ?? 'item';
  const groups = payload?.groups ?? [];
  const currentGroupId = groups.find((group) => group.page.items.some((row) => row.id === itemId))?.id ?? null;
  const term = payload?.board.itemTerminology ?? 'item';

  const close = () => actions.openMenu(null);

  /**
   * `Create new item below` — monday's own wording, and **P-057** its behaviour: the row is
   * inserted directly under the one whose menu is open, not appended to the group. It used to
   * pass `'bottom'` although the comment here already said "after this row", so the third row
   * of a ten-row group created the tenth.
   */
  const createBelow = async () => {
    if (!currentGroupId) return;
    close();
    await actions.addItem(currentGroupId as GroupId, `New ${term}`, { afterItemId: itemId });
  };

  const convertToSubitem = async (parentItemId: ItemId) => {
    close();
    await source.convertItemsToSubitems({
      boardId: actions.boardId as never,
      itemIds: [itemId],
      parentItemId,
    });
    await actions.load();
  };

  /**
   * Every other row of this board is a candidate parent; the item itself is not.
   *
   * **One row per name.** A candidate is offered as its name and nothing else, so two items
   * that happen to share a name are two rows a user cannot tell apart — and picking "the
   * second one" is not a choice the menu lets them express. The first item with each name is
   * the one offered. What monday's own `Convert to subitem ▸` shows (a flat list at all? a
   * searchable picker? the item id beside the name?) was never captured — only the fact that
   * the entry exists (`[c:item-menu-convert-to-subitem]`, whose `tree` is `null`) — so the
   * whole list is this product's invention and **q495** asks for the capture that would
   * replace it. Until then the conservative reading is the unambiguous one.
   */
  const parentCandidates = (() => {
    const byName = new Map<string, (typeof groups)[number]['page']['items'][number]>();
    for (const row of groups.flatMap((group) => group.page.items)) {
      if (row.id === itemId || byName.has(row.name)) continue;
      byName.set(row.name, row);
      if (byName.size >= 30) break;
    }
    return [...byName.values()];
  })();

  const head: MenuEntry[] = contextMenu
    ? [
        {
          id: 'copy-name',
          label: 'Copy name',
          onSelect: () => void navigator.clipboard?.writeText(name).catch(() => undefined),
        },
        { id: 'd0', label: '', divider: true },
      ]
    : [];

  const entries: MenuEntry[] = [
    ...head,
    { id: 'open', label: 'Open item', onSelect: onOpenItem },
    {
      id: 'open-tab',
      label: 'Open in new tab',
      onSelect: () => window.open(`/boards/${payload?.board.id}/pulses/${itemId}`, '_blank'),
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'copy-link',
      label: 'Copy item link',
      onSelect: () =>
        void navigator.clipboard
          ?.writeText(`${window.location.origin}/boards/${payload?.board.id}/pulses/${itemId}`)
          .catch(() => undefined),
    },
    {
      id: 'move',
      label: 'Move to',
      submenuWidth: 240,
      submenu: [
        {
          id: 'move-top',
          label: 'Move to top',
          disabled: currentGroupId === null,
          onSelect: () => {
            close();
            if (currentGroupId) void actions.reorderItem(itemId, currentGroupId as GroupId, null);
          },
        },
        {
          id: 'move-group',
          label: 'Move to group',
          submenu: groups.map((group) => ({
            id: `group-${String(group.id)}`,
            label: group.title,
            disabled: group.id === currentGroupId,
            onSelect: () => {
              close();
              void actions.moveItems([itemId], group.id);
            },
          })),
        },
        {
          /**
           * **P-054.** The picker is the *view's*, not this menu's: selecting a row closes the
           * menu in the same tick (`Menu.tsx`: `if (!entry.submenu) onClose()`), so a dialog
           * rendered from here was unmounted with the menu before it could paint and
           * `Move to board` opened nothing at all.
           */
          id: 'move-board',
          label: 'Move to board',
          onSelect: () => actions.setMovingItems([itemId]),
        },
      ],
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      onSelect: () => void actions.duplicateItems([itemId]),
    },
    { id: 'create-below', label: 'Create new item below', onSelect: () => void createBelow() },
    { id: 'd2', label: '', divider: true },
    {
      id: 'add-subitem',
      label: 'Add subitem',
      onSelect: () => {
        close();
        void actions.addSubitem(itemId, 'New subitem');
      },
    },
    {
      id: 'convert',
      label: 'Convert to subitem',
      // monday asks which item becomes the parent; the list is this board's other items.
      submenu: parentCandidates.map((row) => ({
        id: `parent-${String(row.id)}`,
        label: row.name,
        onSelect: () => void convertToSubitem(row.id),
      })),
    },
    { id: 'd3', label: '', divider: true },
    { id: 'archive', label: 'Archive', onSelect: () => void actions.archiveItems([itemId]) },
    { id: 'delete', label: 'Delete', danger: true, onSelect: () => void actions.deleteItems([itemId]) },
  ];

  /**
   * **P-304.** A subitem's row menu is monday's own, captured; an item's is the one above.
   * One `Menu`, two entry sets — the frame, the geometry and the dismiss rules are shared.
   */
  const rowEntries =
    parentItemId === undefined
      ? entries
      : subitemEntries({
          itemId,
          parentItemId: parentItemId as ItemId,
          boardId: String(payload?.board.id ?? ''),
          subitemsBoardId,
          candidates: groups
            .flatMap((group) => group.page.items)
            .slice(0, 30)
            .map((row) => ({ id: row.id, name: row.name })),
          actions,
          onOpenItem,
          close,
        });

  return (
    <Menu
      entries={rowEntries}
      x={x}
      y={y}
      width={220}
      label={`More options for ${name}`}
      onClose={close}
    />
  );
}
